import { isValidFaireDirectPathExcludingLandingPageAndSignUpRoutes } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/VALID_LOGGED_OUT_FAIRE_DIRECT_ROUTES";
import { useFaireDirectInfo } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectInfo/useFaireDirectInfo";
import {
  FaireDirectSignUpTypes,
  SignUpType,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { brandPage, home } from "@faire/retailer-visitor-shared/paths/index";
import { ProductPagePaths } from "@faire/retailer-visitor-shared/paths/ProductPage";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useOpenWithFaireApplicationStatus } from "@faire/retailer-visitor-shared/stores/domain/OpenWithFaire/useOpenWithFaireApplicationStatus";
import { PROMO_MODAL_LAST_TIME_SEEN_STORAGE_KEY } from "@faire/retailer-visitor-shared/stores/ui/PromoModalStore";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { cleanSignUpParams } from "@faire/retailer-visitor-shared/views/SignUp/_utils/cleanSignUpParams";
import { generateBackLinkFromQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_utils/generateBackLinkFromQueryParams";
import { useFinishedSignupOnboarding } from "@faire/retailer-visitor-shared/views/SignUp/useFinishedSignupOnboarding";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { Storage } from "@faire/web/common/Storage";
import videoPage from "@faire/web-api--source/routes/www/video/videoToken";
import { QueryParameters as SignUpParams } from "@faire/web-api--source/routes/www/welcome/r";
import qs from "query-string";
import { useCallback } from "react";

import IQueries = ProductPagePaths.index.IQueries;

export const useRedirectAfterSignUp = () => {
  const { queryParams } = useSignUpQueryParams();
  const retailerToken = getRetailer()?.token;
  const { faireDirectBrandToken } = useFaireDirectInfo();
  const { hasApplied: hasOwFApplied } = useOpenWithFaireApplicationStatus();
  const { setFinishedSignupOnboarding } = useFinishedSignupOnboarding();

  return useCallback(
    () =>
      redirectAfterSignUp(
        queryParams,
        {
          retailerToken,
          faireDirectBrandToken,
          hasOwFApplied,
        },
        setFinishedSignupOnboarding
      ),
    [
      faireDirectBrandToken,
      hasOwFApplied,
      queryParams,
      retailerToken,
      setFinishedSignupOnboarding,
    ]
  );
};

const redirectAfterSignUp = async (
  queryParams: Partial<
    SignUpParams & {
      utm_medium?: string;
      refP?: string;
    }
  >,
  options: IOptions,
  setFinishedSignupOnboarding: () => void
) => {
  const prioritizedHandlers = [
    handleBrand,
    handleFaireDirect,
    handleProduct,
    handleVideo,
    handleOwF,
    handleBrandInvoice,
    handleDefault,
  ];

  let newLocationRoot = home();

  for (const handler of prioritizedHandlers) {
    const result = handler(queryParams, options);

    const page = await Promise.resolve(result);

    if (page) {
      newLocationRoot = page;
      break;
    }
  }

  setFinishedSignupOnboarding();

  getLocationOrThrow().href = newLocationRoot;
};

type IOptions = {
  retailerToken: string | undefined;
  faireDirectBrandToken: string | undefined;
  hasOwFApplied: boolean;
};

const handleBrand = (queryParams: Partial<SignUpParams>, _: IOptions) => {
  const { signUp, brand } = queryParams;

  if (!brand || signUp !== SignUpType.BRAND) {
    return;
  }

  return brandPage(brand ?? "", cleanSignUpParams(queryParams));
};

const handleFaireDirect = (
  queryParams: Partial<SignUpParams>,
  options: IOptions
) => {
  const { signUp, brand, source } = queryParams;
  const { faireDirectBrandToken } = options;

  if (!brand && !faireDirectBrandToken) {
    return;
  }

  const isFaireDirectSignup = FaireDirectSignUpTypes.includes(
    signUp as SignUpType
  );

  if (!isFaireDirectSignup) {
    return;
  }

  Storage.local.removeItem(PROMO_MODAL_LAST_TIME_SEEN_STORAGE_KEY);

  if (
    source &&
    isValidFaireDirectPathExcludingLandingPageAndSignUpRoutes(
      source,
      faireDirectBrandToken
    )
  ) {
    return handleDefault(queryParams, options);
  }

  return brandPage(
    brand ?? faireDirectBrandToken ?? "",
    cleanSignUpParams(queryParams)
  );
};

const handleProduct = (queryParams: Partial<SignUpParams>, _: IOptions) => {
  const { signUp, product } = queryParams;

  if (signUp !== SignUpType.PRODUCT || !product) {
    return;
  }

  const newLocationQuery = cleanSignUpParams(queryParams);
  const backLink = generateBackLinkFromQueryParams(queryParams);
  newLocationQuery["backLink"] = backLink;

  return ProductPagePaths.index(product, newLocationQuery as IQueries);
};

const handleVideo = (queryParams: Partial<SignUpParams>, _: IOptions) => {
  const { video } = queryParams;

  if (!video) {
    return;
  }

  const newLocationQuery = cleanSignUpParams(queryParams);
  const newLocationQueryString = qs.stringify(newLocationQuery);
  const newQueryString = newLocationQueryString.length
    ? `?${newLocationQueryString}`
    : "";

  return `${videoPage(video)}${newQueryString}`;
};

const handleOwF = (queryParams: Partial<SignUpParams>, options: IOptions) => {
  const { source } = queryParams;
  const { hasOwFApplied } = options;

  if (source !== "/open-with-faire" || !hasOwFApplied) {
    return;
  }

  return home();
};

const handleBrandInvoice = (
  queryParams: Partial<SignUpParams>,
  _: IOptions
) => {
  const { source } = queryParams;

  if (!source?.includes("brand-invoice")) {
    return;
  }

  return home();
};

const handleDefault = (queryParams: Partial<SignUpParams>, _: IOptions) => {
  const { source } = queryParams;
  const newLocationQuery = cleanSignUpParams(queryParams);
  const newLocationQueryString = qs.stringify(newLocationQuery);
  const newQueryString = newLocationQueryString.length
    ? `?${newLocationQueryString}`
    : "";

  return `${source || home()}${newQueryString}`;
};
